import type { PDFDocument, PDFPage } from "pdf-lib";

const FIXED_DIMENSION_TOLERANCE_RATIO = 0.02;
const LETTER_DIMENSION_TOLERANCE_RATIO = 0.03;
const MINIMUM_CROP_DEFICIT_RATIO = 0.15;
const MINIMUM_DOMINANT_CLUSTER_SIZE = 2;

const STANDARD_PAGE_SIZES = [
  { width: 612, height: 792 }, // US Letter
  { width: 612, height: 1008 }, // US Legal
  { width: 595.28, height: 841.89 }, // A4
] as const;

type PageDimensions = {
  width: number;
  height: number;
};

export type NormalizedBoundingBox = readonly [x: number, y: number, width: number, height: number];

export function rotateNormalizedBoundingBox(
  [x, y, width, height]: NormalizedBoundingBox,
  clockwiseDegrees: number
): NormalizedBoundingBox {
  const rotation = ((clockwiseDegrees % 360) + 360) % 360;

  switch (rotation) {
    case 90:
      return [1 - y - height, x, height, width];
    case 180:
      return [1 - x - width, 1 - y - height, width, height];
    case 270:
      return [y, 1 - x - width, height, width];
    default:
      return [x, y, width, height];
  }
}

type DimensionCluster = {
  width: number;
  height: number;
  count: number;
};

function isWithinTolerance(actual: number, expected: number): boolean {
  return Math.abs(actual - expected) / expected <= FIXED_DIMENSION_TOLERANCE_RATIO;
}

function isMateriallyShort(actual: number, expected: number): boolean {
  return (expected - actual) / expected >= MINIMUM_CROP_DEFICIT_RATIO;
}

function getVisibleBoxDimensions(page: PDFPage, box: PageDimensions): PageDimensions {
  const normalizedRotation = ((page.getRotation().angle % 360) + 360) % 360;

  return normalizedRotation === 90 || normalizedRotation === 270
    ? { width: box.height, height: box.width }
    : { width: box.width, height: box.height };
}

function getVisiblePageDimensions(page: PDFPage): PageDimensions {
  return getVisibleBoxDimensions(page, page.getCropBox());
}

function hasMaterialCropBox(page: PDFPage): boolean {
  const cropBox = normalizePageOrientation(getVisibleBoxDimensions(page, page.getCropBox()));
  const mediaBox = normalizePageOrientation(getVisibleBoxDimensions(page, page.getMediaBox()));

  return isMateriallyShort(cropBox.width, mediaBox.width) || isMateriallyShort(cropBox.height, mediaBox.height);
}

function normalizePageOrientation({ width, height }: PageDimensions): PageDimensions {
  return width <= height ? { width, height } : { width: height, height: width };
}

function clusterDimensions(values: PageDimensions[]): DimensionCluster[] {
  const clusters: DimensionCluster[] = [];

  for (const value of values.map(normalizePageOrientation)) {
    const cluster = clusters.find(
      ({ width, height }) => isWithinTolerance(value.width, width) && isWithinTolerance(value.height, height)
    );

    if (cluster) {
      cluster.width = (cluster.width * cluster.count + value.width) / (cluster.count + 1);
      cluster.height = (cluster.height * cluster.count + value.height) / (cluster.count + 1);
      cluster.count += 1;
    } else {
      clusters.push({ ...value, count: 1 });
    }
  }

  return clusters.sort(
    (left, right) => right.count - left.count || right.height - left.height || right.width - left.width
  );
}

function getDominantDimensions(values: PageDimensions[]): PageDimensions | null {
  const [dominant, runnerUp] = clusterDimensions(values);

  if (!dominant) {
    return null;
  }

  const hasMajority =
    dominant.count >= MINIMUM_DOMINANT_CLUSTER_SIZE && (runnerUp === undefined || runnerUp.count !== dominant.count);
  const hasTwoDifferentPageSizes = values.length === 2 && runnerUp !== undefined;

  if (!hasMajority && !hasTwoDifferentPageSizes) {
    return null;
  }

  return { width: dominant.width, height: dominant.height };
}

function matchesPageSize(dimensions: PageDimensions, pageSize: PageDimensions, toleranceRatio: number): boolean {
  const { width, height } = normalizePageOrientation(dimensions);

  return (
    Math.abs(width - pageSize.width) / pageSize.width <= toleranceRatio &&
    Math.abs(height - pageSize.height) / pageSize.height <= toleranceRatio
  );
}

function matchesStandardPageSize(dimensions: PageDimensions): boolean {
  const [letter, ...otherStandardSizes] = STANDARD_PAGE_SIZES;

  return (
    matchesPageSize(dimensions, letter, LETTER_DIMENSION_TOLERANCE_RATIO) ||
    otherStandardSizes.some((size) => matchesPageSize(dimensions, size, FIXED_DIMENSION_TOLERANCE_RATIO))
  );
}

function isMateriallyShortLetterPage({ width, height }: PageDimensions): boolean {
  const letter = STANDARD_PAGE_SIZES[0];

  return isWithinTolerance(width, letter.width) && isMateriallyShort(height, letter.height);
}

function isCroppedRelativeToOtherPages(dimensions: PageDimensions, allPageDimensions: PageDimensions[]): boolean {
  if (matchesStandardPageSize(dimensions)) {
    return false;
  }

  const dominantDimensions = getDominantDimensions(allPageDimensions);

  if (!dominantDimensions) {
    return false;
  }

  const candidate = normalizePageOrientation(dimensions);

  return (
    isMateriallyShort(candidate.width, dominantDimensions.width) ||
    isMateriallyShort(candidate.height, dominantDimensions.height)
  );
}

/**
 * Returns zero-based indices for pages whose visible geometry is materially
 * smaller than the document's media-box or dominant two-dimensional page geometry.
 *
 * A materially smaller CropBox or letter-width page is sufficient even for a
 * single-page document. Two-page documents may use the larger page as their
 * reference geometry, while longer documents require a dominant page size.
 */
export function detectCroppedPageIndices(pdfDocument: PDFDocument): number[] {
  const pages = pdfDocument.getPages();
  const pageDimensions = pages.map(getVisiblePageDimensions);

  return pageDimensions
    .map((dimensions, pageIndex) =>
      hasMaterialCropBox(pages[pageIndex]) ||
      isMateriallyShortLetterPage(dimensions) ||
      isCroppedRelativeToOtherPages(dimensions, pageDimensions)
        ? pageIndex
        : null
    )
    .filter((pageIndex): pageIndex is number => pageIndex !== null);
}
